import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Lock } from 'lucide-react';

/**
 * Full-screen overlay shown while the browser is redirecting to Stripe or
 * PayPal. Reassures the user that the checkout is loading (bank/PayPal
 * pages can take 2-4 s to open) and prevents any double-submit.
 *
 * Props:
 *  - show: boolean — controls visibility
 *  - provider: 'stripe' | 'paypal' — drives the label + accent colour
 */
export default function PaymentRedirectingOverlay({ show, provider = 'stripe' }) {
  const { t } = useTranslation();
  if (!show) return null;

  const providerLabel =
    provider === 'paypal'
      ? 'PayPal'
      : t('booking.stripeLabel', { defaultValue: 'Stripe' });

  const accent = provider === 'paypal' ? '#003087' : '#635BFF';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#1A3C34]/85 backdrop-blur-sm"
      data-testid="payment-redirect-overlay"
      role="dialog"
      aria-modal="true"
      aria-live="polite"
    >
      <div className="max-w-sm w-[92%] bg-white rounded-2xl shadow-2xl p-6 sm:p-8 text-center">
        <div
          className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
          style={{ backgroundColor: `${accent}15` }}
        >
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: accent }} />
        </div>
        <h2 className="font-heading text-xl font-bold text-[#1A3C34] mb-2">
          {t('booking.redirectingTitle', {
            defaultValue: 'Redirection vers {{provider}}…',
            provider: providerLabel,
          })}
        </h2>
        <p className="text-sm text-[#737373] leading-relaxed">
          {t('booking.redirectingHint', {
            defaultValue:
              'Ne fermez pas cette fenêtre — nous ouvrons le paiement sécurisé.',
          })}
        </p>
        <div className="mt-5 inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#F0EFEA] rounded-full text-[11px] text-[#1A3C34] font-semibold">
          <Lock className="w-3 h-3" />
          {t('booking.securePayment', {
            defaultValue: 'Paiement 100% sécurisé',
          })}
        </div>
      </div>
    </div>
  );
}
